import axios from 'axios';
import { gcApi } from 'config/axios';
import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { REQUEST_HAZARDS, REQUEST_HAZARDS_ON_COURSE } from '../actions/ActionTypes';
import { receiveHazards, receiveHazardsFailed } from '../actions';
import { getSemicircleFromDegrees, getDegreesFromSemicircle } from 'Courses/Hazards/HazardsUtils';
import { isChinaUser } from 'utils/ChinaUtil';
import BaiduConvertService from 'Map/utils/BadiuConvertService';
import BaiduGeoconvService from 'Map/utils/BaiduGeoconvService';

/**
 * ref: HazardDTO.java
 * @typedef {Object} HazardDTO
 * @property {number} lat
 * @property {number} lon
 */

/**
 * convert BD09 data points to GCJ02
 * @param {{ northLat: number; southLat: number; eastLon: number; westLon: number }} boundingBox
 */
export function* processBoundingBoxBD09toGCJ02(boundingBox) {
    const inputPoints = [
        { lat: boundingBox.northLat, lon: boundingBox.eastLon },
        { lat: boundingBox.southLat, lon: boundingBox.westLon }
    ];
    const convertService2 = new BaiduGeoconvService();
    /**
     * @type {{ lat: number; lon: number }[]} converted response
     */
    const convertedPoints = yield convertService2.fetchGenerator(inputPoints, { model: 5 });
    return {
        northLat: convertedPoints[0].lat,
        eastLon: convertedPoints[0].lon,
        southLat: convertedPoints[1].lat,
        westLon: convertedPoints[1].lon
    };
}

/**
 *
 * @param {Object[]} responseData
 * @param {HazardDTO[]} [responseData[].hazards]
 * @returns
 */
export function* processBaiduFetchHazards(responseData) {
    if (!responseData || !responseData.length) {
        yield put(receiveHazards(responseData));
        return;
    }

    try {
        /**
         * @type {{ latitude: number; longitude: number }[]} input points
         */
        const inputPoints = [];

        responseData.forEach((geoHash) => {
            if (!geoHash.hazards?.length) {
                return;
            }
            geoHash.hazards.forEach((hazard) => {
                inputPoints.push({
                    latitude: getDegreesFromSemicircle(hazard.lat),
                    longitude: getDegreesFromSemicircle(hazard.lon)
                });
            });
        });

        const convertService = new BaiduConvertService();

        /**
         * @type {{ lat: number; lon: number }[]} converted response
         */
        const convertedPoints = yield convertService.fetchGenerator(inputPoints);

        const newResponseData = structuredClone(responseData);

        let convertedPointIndex = 0;

        newResponseData.forEach((geoHash) => {
            if (!geoHash.hazards?.length) {
                return;
            }
            for (let hazardIndex = 0; hazardIndex < geoHash.hazards.length; hazardIndex += 1) {
                const convertedPoint = convertedPoints[convertedPointIndex + hazardIndex];
                geoHash.hazards[hazardIndex].lat = getSemicircleFromDegrees(convertedPoint.lat);
                geoHash.hazards[hazardIndex].lon = getSemicircleFromDegrees(convertedPoint.lon);
            }
            convertedPointIndex += geoHash.hazards.length;
        });

        yield put(receiveHazards(newResponseData));
    } catch (error) {
        yield put(receiveHazardsFailed());
    }
}

export function* fetchHazards(action) {
    // https://api.gcs.test.garmin.com/community-hazards/v3/api-docs

    try {
        let { northLat, southLat, eastLon, westLon } = action.boundingBox;

        if (isChinaUser()) {
            const gcj02BoundingBox = yield call(processBoundingBoxBD09toGCJ02, action.boundingBox);
            northLat = gcj02BoundingBox.northLat;
            southLat = gcj02BoundingBox.southLat;
            eastLon = gcj02BoundingBox.eastLon;
            westLon = gcj02BoundingBox.westLon;
        }

        const url = `/it-proxy/gcs/community-hazards/fitness/api/v1/findby/bbox?northLat=${northLat}&southLat=${southLat}&eastLon=${eastLon}&westLon=${westLon}`;
        const response = yield axios.call(axios.get, url, {
            baseURL: '/modern'
        });

        if (isChinaUser()) {
            yield fork(processBaiduFetchHazards, response.data.tiles);
            return;
        }

        yield put(receiveHazards(response.data.tiles));
    } catch (e) {
        yield put(receiveHazardsFailed());
    }
}

/**
 *
 * @param {HazardDTO[]} hazardDTOs
 * @returns
 */
export function* processBaiduFetchHazardsOnCourse(hazardDTOs) {
    if (!hazardDTOs || !hazardDTOs.length) {
        yield put(receiveHazards(hazardDTOs));
        return;
    }

    try {
        const inputPoints = hazardDTOs.map((item) => ({
            longitude: item.lon,
            latitude: item.lat
        }));

        const convertService = new BaiduConvertService();

        /**
         * @type {{ lat: number; lon: number }[]} converted response
         */
        const response = yield convertService.fetchGenerator(inputPoints);

        const newHazardDTOs = inputPoints.map((item, index) => ({
            ...hazardDTOs[index],
            lat: response[index].lat,
            lon: response[index].lon
        }));
        yield put(receiveHazards(newHazardDTOs));
    } catch (error) {
        yield put(receiveHazardsFailed());
    }
}

export function* fetchHazardsOnCourse(action) {
    // https://api.gcs.test.garmin.com/community-hazards/v3/api-docs

    try {
        const { courseId } = action;
        const response = yield call(gcApi.get, `course-service/course/${courseId}/hazards`);

        if (isChinaUser()) {
            yield fork(processBaiduFetchHazardsOnCourse, response.data);
            return;
        }

        yield put(receiveHazards(response.data));
    } catch (e) {
        yield put(receiveHazardsFailed());
    }
}

export const hazardsSagas = [
    takeEvery(REQUEST_HAZARDS, fetchHazards),
    takeEvery(REQUEST_HAZARDS_ON_COURSE, fetchHazardsOnCourse)
];
